import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const OrderConfirmation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [orderData, setOrderData] = useState(null);

    useEffect(() => {
        // Recuperar dados do pedido do state
        if (location.state?.order) {
            setOrderData(location.state.order);
        } else {
            // Se não tiver dados, redirecionar para home
            navigate('/');
        }
    }, [location.state, navigate]);

    if (!orderData) {
        return null;
    }

    const formatPrice = (cents) => {
        return (cents / 100).toFixed(2).replace('.', ',');
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatOrderId = (uuid) => {
        if (!uuid) return '';
        // Pegar apenas a primeira parte do UUID antes do primeiro hífen
        return uuid.split('-')[0];
    };

    // Função para determinar o status atual do pedido
    const getOrderStatus = () => {
        const statuses = [
            {
                label: 'Pedido criado',
                active: true,
                completed: true
            },
            {
                label: 'Pagamento confirmado',
                active: false,
                completed: false
            },
            {
                label: 'Enviado',
                active: false,
                completed: false
            },
            {
                label: 'Entregue',
                active: false,
                completed: false
            }
        ];
        return statuses;
    };

    const timelineSteps = getOrderStatus();

    const totalAmount = orderData.amount || 0;
    const shippingCost = orderData.shipping_method?.cost || 0;
    const subtotal = totalAmount - shippingCost;

    return (
        <section className="wrapper style1">
            <div className="container">
                <div style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    padding: '40px 20px'
                }}>
                    {/* Header de sucesso */}
                    <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                        <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            backgroundColor: '#28a745',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 20px'
                        }}>
                            <i className="fas fa-check" style={{ fontSize: '2.5rem', color: '#ffffff' }}></i>
                        </div>
                        <h1 style={{
                            fontSize: '2rem',
                            fontWeight: 'bold',
                            color: '#000000',
                            marginBottom: '10px'
                        }}>
                            Pedido Confirmado!
                        </h1>
                        <p style={{
                            fontSize: '1.1rem',
                            color: '#666666'
                        }}>
                            Seu pedido foi criado com sucesso
                        </p>
                        <p style={{
                            fontSize: '0.9rem',
                            color: '#999999',
                            marginTop: '10px'
                        }}>
                            Você pode acessar este pedido novamente através da URL:{' '}
                            <a
                                href={`/pedido/${orderData.id}`}
                                style={{
                                    fontFamily: 'monospace',
                                    backgroundColor: '#f8f8f8',
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    color: '#000000',
                                    textDecoration: 'underline'
                                }}
                            >
                                /pedido/{formatOrderId(orderData.id)}
                            </a>
                        </p>
                    </div>

                    {/* Informações do pedido */}
                    <div style={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        padding: '30px',
                        marginBottom: '30px'
                    }}>
                        <h2 style={{
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            color: '#000000',
                            marginBottom: '20px',
                            borderBottom: '2px solid #edf507',
                            paddingBottom: '10px'
                        }}>
                            Detalhes do Pedido
                        </h2>

                        <div style={{ marginBottom: '20px' }}>
                            <strong>Número do Pedido:</strong>
                            <div style={{
                                fontFamily: 'monospace',
                                fontSize: '1.2rem',
                                color: '#edf507',
                                backgroundColor: '#000000',
                                padding: '10px',
                                borderRadius: '4px',
                                marginTop: '5px'
                            }}>
                                {formatOrderId(orderData.id)}
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <strong>Data:</strong>
                            <div style={{ color: '#666', marginTop: '5px' }}>
                                {formatDate(orderData.created_at)}
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <strong>Status do Pedido:</strong>
                            <div style={{
                                marginTop: '15px',
                                display: 'flex',
                                gap: '10px',
                                position: 'relative'
                            }}>
                                {/* Linha conectora */}
                                <div style={{
                                    position: 'absolute',
                                    top: '15px',
                                    left: '0',
                                    right: '0',
                                    height: '2px',
                                    backgroundColor: '#e0e0e0',
                                    zIndex: 0
                                }}></div>
                                {timelineSteps.map((step, index) => (
                                    <div key={index} style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '8px',
                                        flex: '1',
                                        minWidth: '120px',
                                        position: 'relative',
                                        zIndex: 1
                                    }}>
                                        <div style={{
                                            width: '30px',
                                            height: '30px',
                                            borderRadius: '50%',
                                            backgroundColor: step.completed ? '#28a745' : step.active ? '#edf507' : '#e0e0e0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: step.completed || step.active ? '#000000' : '#666',
                                            fontWeight: 'bold',
                                            fontSize: '0.9rem',
                                            flexShrink: 0,
                                            border: '2px solid #ffffff',
                                            boxSizing: 'border-box'
                                        }}>
                                            {step.completed ? '✓' : index + 1}
                                        </div>
                                        <div style={{
                                            fontSize: '0.85rem',
                                            fontWeight: step.active ? 'bold' : 'normal',
                                            color: step.completed ? '#28a745' : step.active ? '#000000' : '#666',
                                            textAlign: 'center'
                                        }}>
                                            {step.label}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Informações do cliente */}
                    <div style={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        padding: '30px',
                        marginBottom: '30px'
                    }}>
                        <h2 style={{
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            color: '#000000',
                            marginBottom: '20px',
                            borderBottom: '2px solid #edf507',
                            paddingBottom: '10px'
                        }}>
                            Informações do Cliente
                        </h2>

                        <div style={{ marginBottom: '15px' }}>
                            <strong>Nome:</strong>
                            <div style={{ color: '#666', marginTop: '5px' }}>
                                {orderData.customer?.name}
                            </div>
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <strong>Email:</strong>
                            <div style={{ color: '#666', marginTop: '5px' }}>
                                {orderData.customer?.email}
                            </div>
                        </div>

                        {orderData.customer?.phone && (
                            <div style={{ marginBottom: '15px' }}>
                                <strong>Telefone:</strong>
                                <div style={{ color: '#666', marginTop: '5px' }}>
                                    {orderData.customer.phone}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Produtos */}
                    <div style={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        padding: '30px',
                        marginBottom: '30px'
                    }}>
                        <h2 style={{
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            color: '#000000',
                            marginBottom: '20px',
                            borderBottom: '2px solid #edf507',
                            paddingBottom: '10px'
                        }}>
                            Produtos
                        </h2>

                        {orderData.products?.map((product, index) => (
                            <div key={index} style={{
                                display: 'flex',
                                gap: '15px',
                                padding: '15px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '6px',
                                marginBottom: '10px'
                            }}>
                                <div style={{ flex: '1' }}>
                                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                                        {product.name}
                                    </div>
                                    <div style={{ color: '#666', fontSize: '0.9rem' }}>
                                        SKU: {product.sku}
                                    </div>
                                    <div style={{ color: '#666', fontSize: '0.9rem' }}>
                                        Quantidade: {product.quantity || 1}
                                    </div>
                                </div>
                                <div style={{
                                    fontWeight: 'bold',
                                    color: '#000000'
                                }}>
                                    R$ {formatPrice(product.base_price * (product.quantity || 1))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Resumo financeiro */}
                    <div style={{
                        backgroundColor: '#f8f8f8',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        padding: '30px',
                        marginBottom: '30px'
                    }}>
                        <h2 style={{
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            color: '#000000',
                            marginBottom: '20px',
                            borderBottom: '2px solid #edf507',
                            paddingBottom: '10px'
                        }}>
                            Resumo do Pedido
                        </h2>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '10px',
                            fontSize: '1rem'
                        }}>
                            <span>Subtotal:</span>
                            <span>R$ {formatPrice(subtotal)}</span>
                        </div>

                        {orderData.shipping_method && (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '10px',
                                fontSize: '1rem'
                            }}>
                                <span>Frete ({orderData.shipping_method.name}):</span>
                                <span>R$ {formatPrice(shippingCost)}</span>
                            </div>
                        )}

                        {orderData.discount && orderData.discount.amount > 0 && (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '10px',
                                fontSize: '1rem',
                                color: '#28a745'
                            }}>
                                <span>Desconto:</span>
                                <span>-R$ {formatPrice(orderData.discount.amount)}</span>
                            </div>
                        )}

                        <hr style={{
                            border: '1px solid #e0e0e0',
                            margin: '20px 0'
                        }} />

                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '1.3rem',
                            fontWeight: 'bold',
                            color: '#000000'
                        }}>
                            <span>Total:</span>
                            <span>R$ {formatPrice(totalAmount)}</span>
                        </div>

                        {orderData.payment_methods && orderData.payment_methods.length > 0 && (
                            <div style={{
                                marginTop: '20px',
                                padding: '15px',
                                backgroundColor: '#fffbf0',
                                borderRadius: '6px',
                                border: '1px solid #edf507'
                            }}>
                                <strong>Método de Pagamento:</strong>
                                <div style={{ color: '#666', marginTop: '5px' }}>
                                    {orderData.payment_methods.map((method, index) => (
                                        <div key={index}>
                                            {method.name} - R$ {formatPrice(method.amount)}
                                            <span style={{
                                                color: method.is_paid ? '#28a745' : '#ffc107',
                                                marginLeft: '10px'
                                            }}>
                                                {method.is_paid ? '(Pago)' : '(Pendente)'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Informações de entrega */}
                    {orderData.shipping_method && (
                        <div style={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            padding: '30px',
                            marginBottom: '30px'
                        }}>
                            <h2 style={{
                                fontSize: '1.5rem',
                                fontWeight: 'bold',
                                color: '#000000',
                                marginBottom: '20px',
                                borderBottom: '2px solid #edf507',
                                paddingBottom: '10px'
                            }}>
                                Informações de Entrega
                            </h2>

                            <div style={{ marginBottom: '15px' }}>
                                <strong>Método:</strong>
                                <div style={{ color: '#666', marginTop: '5px' }}>
                                    {orderData.shipping_method.name}
                                </div>
                            </div>

                            {orderData.shipping_method.delivery_days > 0 && (
                                <div style={{ marginBottom: '15px' }}>
                                    <strong>Prazo de entrega:</strong>
                                    <div style={{ color: '#666', marginTop: '5px' }}>
                                        até {orderData.shipping_method.delivery_days} dia(s) após a postagem
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Botões de ação */}
                    <div style={{
                        display: 'flex',
                        gap: '15px',
                        justifyContent: 'center',
                        marginTop: '40px'
                    }}>
                        <button
                            onClick={() => navigate('/')}
                            style={{
                                padding: '15px 40px',
                                backgroundColor: '#edf507',
                                color: '#000000',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#dce406';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#edf507';
                            }}
                        >
                            Continuar Comprando
                        </button>

                        <button
                            onClick={() => window.print()}
                            style={{
                                padding: '15px 40px',
                                backgroundColor: '#ffffff',
                                color: '#000000',
                                border: '2px solid #000000',
                                borderRadius: '8px',
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f8f8f8';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#ffffff';
                            }}
                        >
                            Imprimir Pedido
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default OrderConfirmation;

